import { useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  pullDemoCard,
  type ApiClient,
  type IframePoolOrder,
  type IframePoolOrderItem,
  type PoolOfferDetail,
} from '../api';
import { Button } from '../design-system/Button';
import { useBodyScrollLock } from '../design-system';
import { useBackHandler } from '../backStack';
import { PackReveal } from '../reveal/PackReveal';
import { RevealBackground } from '../reveal/RevealBackground';
import { RevealToggles } from '../reveal/RevealToggles';
import { useIsSleek } from '../theme';
import { DEMO_ORDER_ID, type DemoRevealSource } from '../reveal/demoSource';
import { demoErrorMessage } from './demoErrors';

export interface DemoRevealOverlayProps {
  client: ApiClient;
  offer: PoolOfferDetail;
  embedKey: string | null;
  onClose: () => void;
}

/**
 * The sealed pack the ceremony starts on. `openedAt: null` is what puts the
 * reveal machine in `ready`, so the demo opens the same way a bought pack
 * does — RIP! on the slot lane, or the operator's slice / burst scene. Every
 * other field is filled by the pulled card.
 */
function demoPlaceholderItem(): IframePoolOrderItem {
  return {
    id: DEMO_ORDER_ID,
    title: '',
    imageUrl: null,
    itemPrice: 0,
    openedAt: null,
    fairMarketValue: null,
    status: null,
    badge: null,
    cardDetails: null,
    bestActiveOffer: null,
    poolOrderId: null,
    soldFromPoolOffer: null,
    deliveryInfo: null,
    fairnessTicketId: null,
  };
}

/**
 * Full-screen free demo rip. The shell mirrors `RevealScreen`'s overlay host
 * — same stage box, background, safe area, top-bar switches — and the flow
 * inside it IS the real one: `PackReveal` runs the operator's opening
 * mechanic, sound, rays, glow and badge sequence, and its Open Next Pack
 * loop, off a `DemoRevealSource` instead of a purchase. Nothing here can
 * spend or earn: no order, no balance gate, no sell, no host events.
 *
 * Differences from the real screen are only the ones demo mode implies: the
 * winner's bottom offers "Try Again" and "Pull for Real" where the real one
 * sells or ships the card, there is no balance pill (an anonymous visitor
 * has no balance), and no multi-pack arrows (one free pull at a time).
 */
export function DemoRevealOverlay({ client, offer, embedKey, onClose }: DemoRevealOverlayProps) {
  useBodyScrollLock();
  useBackHandler(true, onClose, 'overlay');
  // Sleek's real reveal has no skip / mute switches (its own top bar), and
  // here they did nothing on sleek — so the demo shows only Exit there too.
  const isSleek = useIsSleek();

  const pullCard = useCallback(async () => {
    if (!embedKey) throw new Error('This demo isn’t available right now.');
    try {
      return await pullDemoCard(client, offer.id, embedKey);
    } catch (err) {
      throw new Error(demoErrorMessage(err));
    }
  }, [client, offer.id, embedKey]);

  const demo = useMemo<DemoRevealSource>(
    () => ({ pullCard, ctaLabel: 'Try Again' }),
    [pullCard],
  );

  // The reveal flow's own offer shape. The video fields it carries are
  // unused by every reveal component, so the catalog payload covers it.
  const revealOffer = useMemo<IframePoolOrder['poolOffer']>(
    () => ({
      id: offer.id,
      title: offer.title,
      price: offer.price,
      packImageUrl: offer.packImageUrl,
      pedestalImageUrl: offer.pedestalImageUrl,
      packVideo: null,
      openPackVideo: null,
      webPackVideo: null,
      webOpenPackVideo: null,
      animation: offer.animation,
    }),
    [offer],
  );

  const placeholder = useMemo(demoPlaceholderItem, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      // Same stage box as RevealScreen's OverlayHost — the ceremony's
      // geometry (pack cap, stage bands) is computed against it.
      className="@container safe-area-pad fixed inset-0 z-50 flex flex-col overflow-hidden text-white [--reveal-pack-max-w:calc((100dvh_-_120px_-_var(--page-safe-top)_-_var(--page-safe-bottom))*421/526*0.8)]"
      role="dialog"
      aria-modal="true"
      aria-label="Free demo rip"
    >
      <RevealBackground />
      <div className="relative z-10 flex flex-1 flex-col">
        <div className="relative flex-1 w-full">
          <PackReveal
            client={client}
            orderId={DEMO_ORDER_ID}
            offer={revealOffer}
            item={placeholder}
            demo={demo}
            onRevealed={() => {}}
            refetchOrder={async () => null}
            onBuyAgain={onClose}
            onShowPack={onClose}
          />
        </div>

        <div className="absolute top-4 inset-x-4 z-30 flex items-center justify-between @[600px]:justify-end @[600px]:gap-3">
          {!isSleek && (
            <div className="order-2 flex items-center gap-2 @[600px]:order-none @[600px]:gap-3">
              <RevealToggles animation={offer.animation} />
            </div>
          )}
          <Button
            variant="glass-frosted"
            size="md"
            onClick={onClose}
            className="order-1 @[600px]:order-none"
          >
            Exit
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
